import { useState } from "react";

import { NavigationLink } from "../NavigationLink";
import { SubMenuItemType } from "../types";

import { MENU_STYLES } from "./constants";
import { useMenuItemLogic } from "./hooks";
import { SubMenuItem } from "./SubMenuItem";
import { MenuItemProps } from "./types";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown";
import { SidebarMenuButton, SidebarMenuItem } from "@/components/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";

interface DropdownMenuItemContentProps {
  subItem: SubMenuItemType;
  isLast: boolean;
}

const DropdownMenuItemContent = ({ subItem, isLast }: DropdownMenuItemContentProps) => {
  const { hasSubItems, getFirstNestedItemHref } = useMenuItemLogic();

  // Handle clickable items
  if (subItem.onClick) {
    return (
      <DropdownMenuItem onClick={subItem.onClick} className={MENU_STYLES.dropdown.item}>
        <SubMenuItem item={subItem} textClassName="text-sm" />
      </DropdownMenuItem>
    );
  }

  // Sub-item with nested items
  if (hasSubItems(subItem)) {
    const firstNestedItemHref = getFirstNestedItemHref(subItem);

    return (
      <div>
        <NavigationLink to={firstNestedItemHref}>
          <DropdownMenuItem className={MENU_STYLES.dropdown.item}>
            <SubMenuItem item={subItem} textClassName="text-sm" skipNavigation />
          </DropdownMenuItem>
        </NavigationLink>
        {/* Show nested items if any */}
        {subItem.items && subItem.items.length > 0 && (
          <div className="ml-6 border-l border-gray-200">
            {subItem.items.map(nestedItem => (
              <NavigationLink key={nestedItem.name} to={nestedItem.href}>
                <DropdownMenuItem className={MENU_STYLES.dropdown.item}>
                  <SubMenuItem
                    item={nestedItem}
                    textClassName="text-xs"
                    iconSize="nested"
                    skipNavigation
                  />
                </DropdownMenuItem>
              </NavigationLink>
            ))}
          </div>
        )}
        {!isLast && <DropdownMenuSeparator />}
      </div>
    );
  }

  // Regular sub-item
  return (
    <NavigationLink to={subItem.href!}>
      <DropdownMenuItem className={MENU_STYLES.dropdown.item}>
        <SubMenuItem item={subItem} textClassName="text-sm" skipNavigation />
      </DropdownMenuItem>
    </NavigationLink>
  );
};

export const CollapsedMenuItem = ({ item, isActive }: MenuItemProps) => {
  const Icon = item.icon;
  const { hasSubItems } = useMenuItemLogic();
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Simple item without sub-items
  if (!hasSubItems(item)) {
    const menuButton = (
      <SidebarMenuButton
        isActive={isActive}
        className={`${MENU_STYLES.menuButton.base} ${MENU_STYLES.menuButton.collapsed}`}
      >
        <Icon className={`${MENU_STYLES.icon.main} ${MENU_STYLES.icon.base}`} />
      </SidebarMenuButton>
    );

    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <NavigationLink to={item.href!}>
              <div className="relative">{menuButton}</div>
            </NavigationLink>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{item.name}</p>
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  // Item with sub-items - dropdown
  return (
    <SidebarMenuItem>
      <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
        <DropdownMenuTrigger asChild>
          <div className="relative" onMouseEnter={() => setDropdownOpen(true)}>
            <SidebarMenuButton
              isActive={isActive}
              className={`${MENU_STYLES.menuButton.base} ${MENU_STYLES.menuButton.collapsed} ${MENU_STYLES.menuButton.hover}`}
            >
              <Icon className={`${MENU_STYLES.icon.main} ${MENU_STYLES.icon.base}`} />
            </SidebarMenuButton>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="start"
          className={MENU_STYLES.dropdown.content}
          onMouseLeave={() => setDropdownOpen(false)}
        >
          <div className={MENU_STYLES.dropdown.header}>{item.name}</div>
          {item.items?.map((subItem, index) => (
            <DropdownMenuItemContent
              key={subItem.name}
              subItem={subItem}
              isLast={index === item.items!.length - 1}
            />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
